import { useRef, useCallback, useEffect } from 'react';
import { livenessWebSocketClient } from '../../../../services/unifiedWebSocketClient';

export const useWebSocketLiveness = ({
  processKey,
  urlServiceLiveness,
  logEvent,
  logError,
  onReportEvent,
  onBundleValidated,
  onVideoUploaded,
  onComplete,
  onError,
  responseToHostError,
  EVENT_TRACER,
}) => {
  const clientRef = useRef(null);
  const isInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);
  const initPromiseRef = useRef(null); // ✅ NUEVO: Guardar promesa de inicialización

  const initializeWebSocket = useCallback(async () => {
    // ✅ Si ya está inicializado, devolver cliente existente
    if (isInitializedRef.current && clientRef.current) {
      console.log('[WS] Ya inicializado, reutilizando');
      return clientRef.current;
    }
    
    // ✅ Si está inicializando, ESPERAR a que termine (en vez de devolver null)
    if (isInitializingRef.current && initPromiseRef.current) {
      console.log('[WS] Inicialización en curso, esperando...');
      return await initPromiseRef.current;
    }
    
    if (!processKey || !urlServiceLiveness) {
      console.warn('[WS] Parámetros faltantes:', { processKey, urlServiceLiveness });
      return null;
    }

    try {
      isInitializingRef.current = true;
      
      console.log('[WS] Inicializando WebSocket');
      onReportEvent(EVENT_TRACER.SERVICE.InitServiceLiveness, 'Initializing liveness WebSocket');

      // ✅ NUEVO: Guardar la promesa de inicialización para que otras llamadas esperen
      initPromiseRef.current = (async () => {
        const client = new livenessWebSocketClient(processKey, urlServiceLiveness, {
          onConnected: (data) => {
            // Solo logs del socket
            console.log('[WS] Conectado', { processKey, timestamp: data?.timestamp });
            onReportEvent(EVENT_TRACER.SERVICE.InitServiceLiveness, 'WebSocket connected');
          },
          onBundleValidated: (data) => {
            // Solo logs del socket - evento crítico
            console.log('[WS] Bundle validado', {
              shouldRecordVideo: data.shouldRecordVideo,
              isAlive: data.isAlive,
              probability: data.probability,
            });
            onReportEvent(EVENT_TRACER.LIVENESS.LivenessCaptureSuccess, 'Bundle validated');
            if (onBundleValidated) onBundleValidated(data);
          },
          onVideoUploaded: (data) => {
            // Solo logs del socket - evento crítico
            console.log('[WS] Video enviado', { videoPath: data.videoPath });
            onReportEvent(EVENT_TRACER.LIVENESS.LivenessFinishCaptureVideo, 'Video uploaded');
            if (onVideoUploaded) onVideoUploaded(data);
          },
          onComplete: (data) => {
            // Solo logs del socket - evento crítico
            console.log('[WS] Proceso completado', {
              decision: data.decision,
              isAlive: data.isAlive,
              isFraud: data.isFraud,
            });
            onReportEvent(EVENT_TRACER.LIVENESS.LivenessSuccess, 'Process completed');
            if (onComplete) onComplete(data);
          },
          onError: (data) => {
            // Solo logs del socket - errores
            console.error('[WS] Error', data);
            onReportEvent(EVENT_TRACER.LIVENESS.LivenessError, `Error: ${data.message}`);
            if (onError) onError(data);
            responseToHostError({
              error: data.message,
              error_code: data.error_code || 'WEBSOCKET_ERROR',
            });
          },
          onClose: (event) => {
            // Solo logs del socket
            console.log('[WS] 🔌 Desconectado', { code: event.code });
            isInitializedRef.current = false;
            isInitializingRef.current = false;
            initPromiseRef.current = null; // ✅ NUEVO: Limpiar promesa
          },
          logger: {
            // NO duplicar logs - el WebSocketClient ya los muestra
            log: () => {},
            error: () => {},
          },
        });

        console.log('[WS] Conectando...');
        await client.connect();
        console.log('[WS] ✅ Listo para uso');

        clientRef.current = client;
        isInitializedRef.current = true;
        isInitializingRef.current = false;

        return client;
      })();

      // ✅ NUEVO: Esperar a que termine la inicialización y devolver el cliente
      return await initPromiseRef.current;

    } catch (error) {
      console.error('[WS] ❌ Error de inicialización:', error);
      onReportEvent(EVENT_TRACER.SERVICE.InitServiceLiveness, `Initialization failed: ${error.message}`);
      
      // Solo mostrar error si no es un timeout o error de red esperado
      if (responseToHostError) {
        responseToHostError({
          error: 'WebSocket initialization failed',
          message: error.message || 'No se pudo conectar al servidor',
          type: 'CONNECTION_ERROR'
        });
      }
      
      isInitializedRef.current = false;
      isInitializingRef.current = false;
      initPromiseRef.current = null; // ✅ NUEVO: Limpiar promesa en error
      
      // NO re-lanzar el error para que no salga "Uncaught in promise"
      return null;
    }
  }, [
    processKey,
    urlServiceLiveness,
    logEvent,
    logError,
    onReportEvent,
    onBundleValidated,
    onVideoUploaded,
    onComplete,
    onError,
    responseToHostError,
    EVENT_TRACER,
  ]);

  const sendBundle = useCallback(async (bundleData, deviceMetadata) => {
    if (!clientRef.current) {
      throw new Error('WebSocket not initialized');
    }

    try {
      console.log('[WS] Enviando bundle...');
      await clientRef.current.sendBundle(bundleData, deviceMetadata);
      console.log('[WS] Bundle enviado');
    } catch (error) {
      console.error('[WS]Error enviando bundle:', error);
      throw error;
    }
  }, []);

  const sendVideo = useCallback(async (videoBlob, onProgress) => {
    if (!clientRef.current) {
      throw new Error('WebSocket not initialized');
    }

    try {
      console.log('[WS] Enviando video...', { size: videoBlob.size });
      await clientRef.current.sendVideo(videoBlob, (progress) => {
        if (onProgress) onProgress(progress);
      });
      console.log('[WS] Video enviado');
    } catch (error) {
      console.error('[WS] Error enviando video:', error);
      throw error;
    }
  }, []);

  const closeWebSocket = useCallback(() => {
    try {
      if (clientRef.current) {
        console.log('[WS] Cerrando conexión...');
        clientRef.current.close();
        clientRef.current = null;
        isInitializedRef.current = false;
        isInitializingRef.current = false;
        console.log('[WS] Conexión cerrada');
      }
    } catch (error) {
      console.error('[WS] Error cerrando conexión:', error);
    }
  }, []);

  const isConnected = useCallback(() => {
    return clientRef.current?.isConnected || false;
  }, []);

  useEffect(() => {
    return () => {
      closeWebSocket();
    };
  }, [closeWebSocket]);

  return {
    clientRef,
    initializeWebSocket,
    sendBundle,
    sendVideo,
    closeWebSocket,
    isConnected,
  };
};

export default useWebSocketLiveness;
